/**
 * Colour maths for runtime chamber theming.
 *
 * A chamber gives us one hex value. From it we have to produce a hover state, a soft
 * tint, a hairline and — most importantly — decide whether white or ink text sits on
 * top of it. Doing that in sRGB produces muddy tints and wrong contrast calls, because
 * sRGB lightness does not match perceived lightness: #0000FF and #FFFF00 have similar
 * sRGB "brightness" and wildly different perceived ones.
 *
 * So we convert to OKLab/OKLCH, which is perceptually uniform, adjust lightness and
 * chroma there, and convert back. A 20% lightness step looks like a 20% step at every
 * hue, which is what makes a derived ramp usable rather than merely generated.
 */

export interface Rgb {
  r: number;
  g: number;
  b: number;
}
export interface Oklch {
  l: number;
  c: number;
  h: number;
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

export function hexToRgb(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return { r: ((n >> 16) & 255) / 255, g: ((n >> 8) & 255) / 255, b: (n & 255) / 255 };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const to = (v: number) =>
    Math.round(clamp01(v) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

const srgbToLinear = (v: number) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
const linearToSrgb = (v: number) =>
  v <= 0.0031308 ? v * 12.92 : 1.055 * Math.sign(v) * Math.abs(v) ** (1 / 2.4) - 0.055;

export function rgbToOklch(rgb: Rgb): Oklch {
  const r = srgbToLinear(rgb.r);
  const g = srgbToLinear(rgb.g);
  const b = srgbToLinear(rgb.b);

  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

  const c = Math.sqrt(A * A + B * B);
  let h = (Math.atan2(B, A) * 180) / Math.PI;
  if (h < 0) h += 360;
  return { l: L, c, h };
}

export function oklchToRgb({ l: L, c, h }: Oklch): Rgb {
  const hr = (h * Math.PI) / 180;
  const A = c * Math.cos(hr);
  const B = c * Math.sin(hr);

  const l_ = L + 0.3963377774 * A + 0.2158037573 * B;
  const m_ = L - 0.1055613458 * A - 0.0638541728 * B;
  const s_ = L - 0.0894841775 * A - 1.291485548 * B;

  const l = l_ ** 3;
  const m = m_ ** 3;
  const s = s_ ** 3;

  return {
    r: linearToSrgb(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    g: linearToSrgb(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    b: linearToSrgb(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  };
}

export function hexToOklch(hex: string): Oklch | null {
  const rgb = hexToRgb(hex);
  return rgb ? rgbToOklch(rgb) : null;
}

export function oklchToHex(value: Oklch): string {
  return rgbToHex(oklchToRgb(value));
}

/** WCAG 2.1 relative luminance. */
export function relativeLuminance(rgb: Rgb): number {
  const r = srgbToLinear(rgb.r);
  const g = srgbToLinear(rgb.g);
  const b = srgbToLinear(rgb.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const ra = hexToRgb(a);
  const rb = hexToRgb(b);
  if (!ra || !rb) return 1;
  const la = relativeLuminance(ra);
  const lb = relativeLuminance(rb);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

export const WCAG_AA_NORMAL = 4.5;
export const WCAG_AA_LARGE = 3;

/**
 * Picks the readable foreground for a background. Not a lightness threshold — an actual
 * contrast comparison, because a mid-tone brand colour can fail against both and we want
 * to know which fails *less* badly before we fall back.
 */
export function readableOn(background: string, light = '#F4F5EC', dark = '#14301E'): string {
  return contrastRatio(background, light) >= contrastRatio(background, dark) ? light : dark;
}

/**
 * Derives the six brand properties from a chamber's single primary colour.
 *
 * `hover` darkens in OKLCH lightness — for a very dark brand it lightens instead, since
 * darkening #0A0A0A produces no visible hover state at all.
 * `soft` and `line` keep the hue, drop the chroma hard and raise the lightness, which is
 * what makes a tint read as "a pale version of that colour" rather than as grey.
 */
export interface BrandRamp {
  brand: string;
  brandHover: string;
  brandSoft: string;
  brandLine: string;
  brandOn: string;
  contrast: number;
  /** True when the supplied colour cannot carry legible text at any foreground. */
  degraded: boolean;
}

export function deriveBrandRamp(primaryHex: string): BrandRamp | null {
  const base = hexToOklch(primaryHex);
  if (!base) return null;

  const hoverL = base.l < 0.28 ? Math.min(0.92, base.l + 0.06) : Math.max(0.04, base.l - 0.055);
  const brand = oklchToHex(base);
  const brandOn = readableOn(brand);
  const contrast = contrastRatio(brand, brandOn);

  return {
    brand,
    brandHover: oklchToHex({ ...base, l: hoverL }),
    brandSoft: oklchToHex({ l: Math.min(0.965, base.l + (0.95 - base.l) * 0.9), c: Math.min(base.c * 0.22, 0.035), h: base.h }),
    brandLine: oklchToHex({ l: Math.min(0.9, base.l + (0.9 - base.l) * 0.72), c: Math.min(base.c * 0.35, 0.05), h: base.h }),
    brandOn,
    contrast,
    // Below AA for normal text on the brand ground. Real risk: a chamber uploads a
    // mid-yellow, and every primary button becomes unreadable.
    degraded: contrast < WCAG_AA_NORMAL,
  };
}

/**
 * The accent is used for small highlights on a light ground, so it is the *text on the
 * accent* and the accent against paper that matter. A chamber's secondary colour is
 * lightened until it can host its own ink legibly.
 */
export function deriveAccent(secondaryHex: string): { accent: string; accentOn: string } | null {
  const base = hexToOklch(secondaryHex);
  if (!base) return null;
  let candidate = base;
  let accent = oklchToHex(candidate);
  let accentOn = readableOn(accent);
  let guard = 0;
  while (contrastRatio(accent, accentOn) < WCAG_AA_LARGE && guard < 12) {
    candidate = { ...candidate, l: Math.min(0.96, candidate.l + 0.05) };
    accent = oklchToHex(candidate);
    accentOn = readableOn(accent);
    guard += 1;
  }
  return { accent, accentOn };
}
